/**
 * View Role Template - Platform admin view for role template details
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, AdminPageLayout, Card, Badge, Section, Grid, Col } from '../../components';
import { Shield, Users, Activity, Clock } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const ViewRoleTemplate = () => {
  const navigate = useNavigate();
  const { id: templateId } = useParams();
  const { showSuccess, showError } = useToast();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const templateData = await apiService.getRoleTemplate(templateId);
      setTemplate(templateData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleActivate = async () => {
    try {
      await apiService.updateRoleTemplate(templateId, { is_active: true });
      showSuccess('Role template activated successfully');
      loadTemplate();
    } catch (err) {
      showError(`Failed to activate template: ${err.message}`);
    }
  };

  const handleDeactivate = async () => {
    try {
      await apiService.updateRoleTemplate(templateId, { is_active: false });
      showSuccess('Role template deactivated successfully');
      loadTemplate();
    } catch (err) {
      showError(`Failed to deactivate template: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="SCRUTINIZE ROLE ARCHITECTURE" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Synchronizing governance records...</div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout title="SCRUTINIZE ROLE ARCHITECTURE" maxWidth="1400px">
        <Section title="Synchronisation Error">
          <Card level={1}>
            <p style={{ color: 'var(--color-error)', marginBottom: '1.5rem' }}>Failed to retrieve pattern: {error}</p>
            <Button onClick={loadTemplate} variant="primary">Attempt Resync</Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  if (!template) {
    return (
      <AdminPageLayout title="View Role Template" maxWidth="1400px">
        <Section title="Role Not Found">
          <Card level={1}>
            <p style={{ opacity: 0.6, marginBottom: '1.5rem' }}>The requested role template does not exist.</p>
            <Button onClick={() => navigate('/admin/role-templates')} variant="outline">
              Return to Templates
            </Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="SCRUTINIZE ROLE ARCHITECTURE"
      subtitle={template.name}
      backPath="/admin/role-templates"
      maxWidth="1400px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>

        <Section
          title="Governance Status"
          actions={
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button
                onClick={() => navigate(`/admin/role-templates/${templateId}/edit`)}
                variant="edit"
              >
                Modify Architecture
              </Button>
              {template.is_active ? (
                <Button
                  onClick={handleDeactivate}
                  variant="warning"
                >
                  Suspend Pattern
                </Button>
              ) : (
                <Button
                  onClick={handleActivate}
                  variant="success"
                >
                  Activate Pattern
                </Button>
              )}
            </div>
          }
        >
          <Card level={1}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'var(--surface-lvl2)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--color-border-light)'
              }}>
                <Shield size={32} style={{ color: 'var(--color-primary)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>{template.name}</div>
                <div style={{ opacity: 0.6, fontSize: '0.95rem' }}>Role Template • Type: {template.is_staff ? 'Staff Role' : 'Member Role'}</div>
              </div>
              <Badge variant={template.is_active ? 'success' : 'warning'} size="sm">
                {template.is_active ? 'OPERATIONAL' : 'SUSPENDED'}
              </Badge>
            </div>
          </Card>
        </Section>

        <Grid columns={12} gap="2.5rem">
          <Col span={7}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <Section title="Conceptual Definition">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Scope of Authority</div>
                      <p style={{ lineHeight: 1.6, fontSize: '1rem' }}>{template.description || 'No conceptual narrative provided for this role pattern.'}</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', padding: '1.25rem', background: 'var(--surface-lvl2)', borderRadius: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <Users size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase' }}>Current Deployment</div>
                          <div style={{ fontWeight: 600 }}>Active in {template.usage_count} Entities</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <Activity size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase' }}>Authority Level</div>
                          <div style={{ fontWeight: 600 }}>{template.is_staff ? 'Administrative / Staff' : 'Standard User'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Section>

              <Section title={`Authorized Capabilities (${template.permissions.length})`}>
                <Card level={1}>
                  {template.permissions.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                      {template.permissions.map(permission => (
                        <Badge key={permission} variant="info" size="sm">{permission}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p style={{ opacity: 0.5, fontStyle: 'italic' }}>This role architecture contains no specific capabilities.</p>
                  )}
                </Card>
              </Section>
            </div>
          </Col>

          <Col span={5}>
            <Section title="Governance Metadata">
              <Card level={1}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ padding: '0.5rem', background: 'var(--surface-lvl2)', borderRadius: '8px' }}>
                      <Users size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Provisioned By</div>
                      <div style={{ fontWeight: 600 }}>{template.created_by || 'System Core'}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ padding: '0.5rem', background: 'var(--surface-lvl2)', borderRadius: '8px' }}>
                      <Clock size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Initialised Date</div>
                      <div style={{ fontWeight: 600 }}>{new Date(template.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border-light)' }}>
                    <div style={{ padding: '0.5rem', background: 'var(--surface-lvl2)', borderRadius: '8px' }}>
                      <Activity size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Last Refinement</div>
                      <div style={{ fontWeight: 600 }}>{new Date(template.updated_at).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </Card>
            </Section>
          </Col>
        </Grid>
      </div>
    </AdminPageLayout>
  );
};

export default ViewRoleTemplate;